import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiRoutes } from '@/api/routes';
import { useApiErrorMessage } from '@/api/use-api-error';
import { Alert, AlertDescription } from '@/design/components/alert';
import { Button } from '@/design/components/button';
import { Input } from '@/design/components/input';
import { Label } from '@/design/components/label';
import { AuthCard } from '@/features/auth/auth-card';
import { useSessionContext } from '@/features/auth/session';

/**
 * Choosing the PIN that unlocks this terminal after an idle lock.
 *
 * **It is asked once, and it is not optional.** AD-54 replaced the logout at a gate terminal with a
 * PIN prompt precisely so that coming back costs six digits rather than a full password typed on a
 * shared keyboard with a queue watching. An account with no PIN can only ever be unlocked with the
 * password, which is the behaviour the design moved away from — so the account is asked for one
 * before it starts working, in the same sequence as the forced password change and the
 * authenticator enrolment.
 *
 * **It is NOT one of the server's three session gates.** The server does not refuse a session that
 * has no PIN, and it should not: an administrator resetting a password over the telephone must be
 * able to work, and `auth/lockout.ts` treats a PIN as a convenience rather than as a credential of
 * its own. What this screen is, therefore, is a step this application asks for, and the unlock path
 * still falls back to the password for anybody who has none.
 *
 * **The current password is required by the contract**, because setting a PIN is setting a
 * credential for this account and the session alone is not proof enough of who is at the keyboard.
 */
export function PinSetupPage(): ReactNode {
  const { t } = useTranslation();
  const { client, adoptUser } = useSessionContext();
  const describe = useApiErrorMessage();

  const [currentPassword, setCurrentPassword] = useState('');
  const [pin, setPin] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(): Promise<void> {
    setFailure(null);
    setSubmitting(true);
    try {
      // The route answers with the ACCOUNT, not a session: setting a PIN rotates nothing. What
      // the application needs from it is `hasPin`, which is what moves this terminal past this
      // screen.
      adoptUser(await client.request(apiRoutes.authSetPin, { body: { currentPassword, pin } }));
    } catch (error) {
      setFailure(describe(error));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard title={t('auth.pinTitle')} description={t('auth.pinSubtitle')}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        noValidate
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="pin-password">{t('auth.currentPassword')}</Label>
          <Input
            id="pin-password"
            type="password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(event) => {
              setCurrentPassword(event.target.value);
            }}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="pin-value">{t('auth.pin')}</Label>
          <Input
            id="pin-value"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            // Six, which is what `pinSchema` and `crypto/password.ts`'s `PIN_LENGTH` both say a
            // gate PIN is. It read eight, so an operator could type a PIN this product cannot
            // store and be answered a refusal that named no field.
            maxLength={6}
            required
            value={pin}
            onChange={(event) => {
              setPin(event.target.value);
            }}
          />
        </div>

        {failure !== null && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{failure}</AlertDescription>
          </Alert>
        )}

        <Button type="submit" disabled={submitting}>
          {t('auth.setPin')}
        </Button>
      </form>
    </AuthCard>
  );
}
